<template>
    <div class="page-container" v-if="!isLoading">
        <div class="page-form-column">
            <div class="back-link-container">
                <router-link :to="{ name: 'Order Details', params: { orderId: $route.params.orderId } }" class="back-link">
                    <md-icon>arrow_back</md-icon>
                    <span>Back to Order Details</span>
                </router-link>
            </div>
            <md-card>
                <md-card-header>
                    <div class="md-title">Order Information</div>
                </md-card-header>
                <md-card-content>
                    <div class="md-layout md-gutter">
                        <div class="md-layout-item md-medium-size-33 md-small-size-100">
                            <form-group name="orderNumber" label="Order Number">
                                <md-input :value="orderDetails.orderNumber" disabled />
                            </form-group>
                        </div>
                        <div class="md-layout-item md-medium-size-33 md-small-size-100">
                            <form-group name="sourceReference" label="Source Reference">
                                <md-input v-model="orderDetails.sourceReference" />
                            </form-group>
                        </div>
                        <div class="md-layout-item md-medium-size-33 md-small-size-100">
                            <form-group name="purchaseOrderNumber" label="Purchase Order Number">
                                <md-input v-model="orderDetails.purchaseOrderNumber" />
                            </form-group>
                        </div>
                    </div>
                    <div class="md-layout md-gutter">
                        <div class="md-layout-item md-medium-size-50 md-small-size-100">
                            <form-group name="teamRegion" label="Team Region">
                                <team-region-options
                                    :selected="orderDetails.teamRegionId"
                                    @selectedOption="handleTeamRegionChanged"
                                />
                            </form-group>
                        </div>
                        <div class="md-layout-item md-medium-size-50 md-small-size-100">
                            <form-group name="handlingMethod" label="Handling Method">
                                <md-input v-model="orderDetails.handlingMethod" type="hidden" />
                                <vue-select
                                    :options="handlingMethodOptions"
                                    v-model="orderDetails.handlingMethod"
                                    label="handling method"
                                ></vue-select>
                            </form-group>
                        </div>
                    </div>
                    <div class="md-layout md-gutter">
                        <div class="md-layout-item md-medium-size-50 md-small-size-100">
                            <CustomerOptions
                                :selected="orderDetails.customerId"
                                :team-region-id="orderDetails.teamRegionId"
                                @selectedOption="handleCustomerChanged"
                            />
                        </div>
                        <div class="md-layout-item md-medium-size-50 md-small-size-100">
                            <charging-type-options
                                :selected-charge-type-id="orderDetails.rateGroupId"
                                @selectedOption="handleRateTypeChanged"
                            />
                        </div>
                    </div>
                </md-card-content>
            </md-card>

            <md-card>
                <md-card-header>
                    <h3 class="md-title">Schedule & Dates</h3>
                </md-card-header>
                <md-card-content>
                    <div class="md-layout md-gutter">
                        <div class="md-layout-item md-medium-size-50 md-small-size-100">
                            <md-datepicker
                                v-model="orderDetails.orderDate"
                                md-immediately
                                :md-debounce="10"
                            >
                                <label>Order Date</label>
                            </md-datepicker>
                        </div>
                        <div class="md-layout-item md-medium-size-50 md-small-size-100">
                            <md-datepicker
                                v-model="orderDetails.expectedDeliveryDate"
                                md-immediately
                                :md-debounce="10"
                            >
                                <label>Expected Delivery Date</label>
                            </md-datepicker>
                            <div v-if="hasAssignedDropStop" class="md-helper-text-warning">
                                This delivery is already assigned to a team member. Updating the expected delivery date will impact the team member's trip.
                            </div>
                        </div>
                    </div>
                    <div class="md-layout md-gutter">
                        <div class="md-layout-item md-medium-size-50 md-small-size-100">
                            <md-datepicker
                                v-model="orderDetails.readyPickupAvailableDate"
                                md-immediately
                                :md-debounce="10"
                            >
                                <label>Ready Pickup Date</label>
                            </md-datepicker>
                        </div>
                        <div class="md-layout-item md-medium-size-50 md-small-size-100">
                            <md-datepicker
                                v-model="orderDetails.readyDeliveryAvailableDate"
                                md-immediately
                                :md-debounce="10"
                            >
                                <label>Ready Delivery Date</label>
                            </md-datepicker>
                        </div>
                    </div>
                </md-card-content>
            </md-card>

            <md-card>
                <md-card-header>
                    <h3 class="md-title">
                        Pickup Address
                        <span class="optional-label">(Optional)</span>
                    </h3>
                    <md-checkbox v-model="hasPickupAddress" class="pickup-checkbox">
                        Enable pickup address
                    </md-checkbox>
                </md-card-header>
                <md-card-content v-if="hasPickupAddress">
                    <div class="md-layout md-gutter">
                        <div class="md-layout-item">
                            <google-autocomplete
                                label="Pickup Address"
                                id="pickupAddress"
                                classname="form-control autocomplete-input"
                                placeholder=""
                                :should-focus="false"
                                v-model="orderDetails.pickupAddress"
                                @placechanged="handlePickupAddressChange"
                            ></google-autocomplete>
                        </div>
                    </div>
                    <div class="md-layout md-gutter">
                        <div class="md-layout-item md-medium-size-33 md-small-size-100">
                            <form-group name="pickupContactName" label="Pickup Contact Name">
                                <md-input v-model="orderDetails.pickupContact.name" />
                            </form-group>
                        </div>
                        <div class="md-layout-item md-medium-size-33 md-small-size-100">
                            <form-group name="pickupContactPhone" label="Pickup Contact Phone">
                                <md-input v-model="orderDetails.pickupContact.phone" />
                            </form-group>
                        </div>
                        <div class="md-layout-item md-medium-size-33 md-small-size-100">
                            <form-group name="pickupContactEmail" label="Pickup Contact Email">
                                <md-input v-model="orderDetails.pickupContact.email" type="email" />
                            </form-group>
                        </div>
                    </div>
                    <div class="md-layout md-gutter">
                        <div class="md-layout-item md-medium-size-50 md-small-size-100">
                            <form-group name="pickupTimeWindowStart" label="Pickup Time Window Start" class="time-picker hide-clear-btn">
                                <time-picker
                                    :time="pickupTimeWindowStart"
                                    :all-time-options="false"
                                    :is-required="false"
                                    @selectedTime="getPickupTimeWindowStart"
                                />
                            </form-group>
                        </div>
                        <div class="md-layout-item md-medium-size-50 md-small-size-100">
                            <form-group name="pickupTimeWindowEnd" label="Pickup Time Window End" class="time-picker hide-clear-btn">
                                <time-picker
                                    :time="pickupTimeWindowEnd"
                                    :all-time-options="false"
                                    :is-required="false"
                                    @selectedTime="getPickupTimeWindowEnd"
                                />
                            </form-group>
                        </div>
                    </div>
                    <span class="timezone-note">Time window is displayed in the timezone of the pickup address.</span>
                </md-card-content>
            </md-card>

            <md-card>
                <md-card-header>
                    <h3 class="md-title">
                        Delivery Address
                        <span class="optional-label">(Optional)</span>
                    </h3>
                    <md-checkbox v-model="hasDeliveryAddress" class="pickup-checkbox">
                        Enable delivery address
                    </md-checkbox>
                </md-card-header>
                <md-card-content v-if="hasDeliveryAddress">
                    <div class="md-layout md-gutter">
                        <div class="md-layout-item">
                            <google-autocomplete
                                label="Delivery Address"
                                id="shippingAddress"
                                classname="form-control autocomplete-input"
                                placeholder=""
                                :should-focus="false"
                                v-model="orderDetails.shippingAddress"
                                @placechanged="handleShippingAddressChange"
                            ></google-autocomplete>
                        </div>
                    </div>
                    <div class="md-layout md-gutter">
                        <div class="md-layout-item md-medium-size-33 md-small-size-100">
                            <form-group name="contactName" label="Contact Name">
                                <md-input v-model="orderDetails.contact.name" />
                            </form-group>
                        </div>
                        <div class="md-layout-item md-medium-size-33 md-small-size-100">
                            <form-group name="contactPhone" label="Contact Phone">
                                <md-input v-model="orderDetails.contact.phone" />
                            </form-group>
                        </div>
                        <div class="md-layout-item md-medium-size-33 md-small-size-100">
                            <form-group name="contactEmail" label="Contact Email">
                                <md-input v-model="orderDetails.contact.email" type="email" />
                            </form-group>
                        </div>
                    </div>
                    <div class="md-layout md-gutter">
                        <div class="md-layout-item md-medium-size-50 md-small-size-100">
                            <form-group name="timeWindowStart" label="Delivery Time Window Start" class="time-picker hide-clear-btn">
                                <time-picker
                                    :time="deliveryTimeWindowStart"
                                    :all-time-options="false"
                                    :is-required="false"
                                    @selectedTime="getDeliveryTimeWindowStart"
                                />
                            </form-group>
                        </div>
                        <div class="md-layout-item md-medium-size-50 md-small-size-100">
                            <form-group name="timeWindowEnd" label="Delivery Time Window End" class="time-picker hide-clear-btn">
                                <time-picker
                                    :time="deliveryTimeWindowEnd"
                                    :all-time-options="false"
                                    :is-required="false"
                                    @selectedTime="getDeliveryTimeWindowEnd"
                                />
                            </form-group>
                        </div>
                    </div>
                    <span class="timezone-note">Time window is displayed in the timezone of the delivery address.</span>
                </md-card-content>
            </md-card>

            <md-card>
                <md-card-header>
                    <h3 class="md-title">Additional Details</h3>
                </md-card-header>
                <md-card-content>
                    <div v-if="loadFieldList !== null && loadFieldList.length > 0" class="load-inputs-row">
                        <load-inputs
                            v-for="(load, index) in loadFieldList"
                            :key="index"
                            :load-definition="load"
                            :initial-value="loadValues"
                            @changed="handleLoadChanged"
                        />
                    </div>
                    <form-group v-if="skillOptions.length" name="skills" label="Required Skills">
                        <multiselect
                            v-model="orderDetails.skills"
                            :options="skillOptions"
                            :multiple="true"
                            :close-on-select="true"
                            placeholder="Pick required skills"
                        ></multiselect>
                    </form-group>
                    <form-group name="deliveryInstructions" label="Delivery Instructions">
                        <md-textarea v-model="orderDetails.deliveryInstructions" />
                    </form-group>
                    <form-group name="notes" label="Notes">
                        <md-textarea v-model="orderDetails.notes" />
                    </form-group>
                </md-card-content>
            </md-card>

            <md-card>
                <md-card-header>
                    <h3 class="md-title">Items</h3>
                    <md-button class="md-primary md-just-icon md-round" @click="addItem">
                        <md-icon>add</md-icon>
                    </md-button>
                </md-card-header>
                <md-card-content>
                    <div v-if="orderDetails.items.length === 0" class="no-items">
                        <p>No items added yet. Click the + button to add an item.</p>
                    </div>
                    <div v-else class="items-list">
                        <div
                            v-for="(item, index) in orderDetails.items"
                            :key="index"
                            class="item-entry"
                        >
                            <div class="item-header">
                                <span class="item-number">
                                    Item {{ index + 1 }}
                                    <span v-if="item.status" class="item-status-badge">{{ item.status }}</span>
                                </span>
                                <div class="item-actions">
                                    <md-button
                                        class="md-icon-button md-dense"
                                        @click="duplicateItem(index)"
                                        title="Duplicate item"
                                    >
                                        <md-icon>content_copy</md-icon>
                                    </md-button>
                                    <md-button
                                        class="md-icon-button md-dense md-danger"
                                        @click="removeItem(index)"
                                        :disabled="item.itemId && item.status && item.status !== 'Created'"
                                        :title="item.itemId && item.status && item.status !== 'Created' ? 'Cannot remove: item status is ' + item.status : 'Delete item'"
                                    >
                                        <md-icon>delete</md-icon>
                                    </md-button>
                                </div>
                            </div>
                            <div class="md-layout md-gutter">
                                <div class="md-layout-item md-medium-size-50 md-small-size-100">
                                    <form-group name="barcode" label="Barcode">
                                        <md-input v-model="item.barcode" />
                                    </form-group>
                                </div>
                                <div class="md-layout-item md-medium-size-50 md-small-size-100">
                                    <form-group name="sku" label="SKU">
                                        <md-input v-model="item.sku" />
                                    </form-group>
                                </div>
                            </div>
                            <div class="md-layout md-gutter">
                                <div class="md-layout-item md-size-100">
                                    <form-group name="description" label="Description">
                                        <md-input v-model="item.description" />
                                    </form-group>
                                </div>
                            </div>
                            <div v-if="itemDimensionFields && itemDimensionFields.length > 0" class="item-dimensions">
                                <div class="dimensions-header">
                                    <span class="dimensions-label">Dimensions</span>
                                </div>
                                <div class="dimensions-inputs">
                                    <div
                                        v-for="loadDef in itemDimensionFields"
                                        :key="loadDef.type"
                                        class="load-vals"
                                    >
                                        <form-group :name="loadDef.type" :label="loadDef.label">
                                            <md-input
                                                v-model="item.load[loadDef.type]"
                                                type="number"
                                                step="0.01"
                                                min="0"
                                            />
                                            <span>{{ loadDef.symbol }}</span>
                                        </form-group>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </md-card-content>
            </md-card>
        </div>

        <div>
            <div class="summary">
                <md-card>
                    <md-card-header>
                        <h3 class="md-title">Summary</h3>
                    </md-card-header>
                    <md-card-content>
                        <div class="summary-section" v-if="orderDetails.orderNumber">
                            <h6>Order Number</h6>
                            <p>{{ orderDetails.orderNumber }}</p>
                        </div>
                        <div class="summary-section" v-if="orderDetails.status">
                            <h6>Status</h6>
                            <p>{{ orderDetails.status }}</p>
                        </div>
                        <div class="summary-section" v-if="orderDetails.orderDate">
                            <h6>Order Date</h6>
                            <p>{{ formatDisplayDate(orderDetails.orderDate) }}</p>
                        </div>
                        <div class="summary-section" v-if="orderDetails.expectedDeliveryDate">
                            <h6>Expected Delivery</h6>
                            <p>{{ formatDisplayDate(orderDetails.expectedDeliveryDate) }}</p>
                        </div>
                        <div class="summary-section" v-if="hasDeliveryAddress && orderDetails.shippingAddress && orderDetails.shippingAddress.address">
                            <h6>Delivery Address</h6>
                            <p>{{ orderDetails.shippingAddress.address }}</p>
                        </div>
                        <div class="summary-section" v-if="hasPickupAddress && orderDetails.pickupAddress && orderDetails.pickupAddress.address">
                            <h6>Pickup Address</h6>
                            <p>{{ orderDetails.pickupAddress.address }}</p>
                        </div>
                        <div class="summary-section" v-if="orderDetails.items && orderDetails.items.length > 0">
                            <h6>Items</h6>
                            <p>{{ orderDetails.items.length }} item(s)</p>
                        </div>
                    </md-card-content>
                    <md-card-actions>
                        <md-button class="md-primary" @click="updateOrder" :disabled="isUpdating">
                            <md-progress-spinner
                                v-if="isUpdating"
                                :md-diameter="20"
                                :md-stroke="2"
                                md-mode="indeterminate"
                            ></md-progress-spinner>
                            <span v-else>Update Order</span>
                        </md-button>
                    </md-card-actions>
                </md-card>
            </div>
        </div>
    </div>
    <div v-else class="loader-container">
        <fade-loader :loading="true" color="#333333" />
        <span>LOADING</span>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import { handleRequests, showErrorMessage } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { DATE_TYPES } from '@/utils/constants';
import {
    TimePicker,
    LoadInputs,
    ChargingTypeOptions
} from '@/components';
import TeamRegionOptions from '@/components/TeamRegionOptions';
import GoogleAutocomplete from '@/components/Autocomplete/GoogleAutocomplete';
import CustomerOptions from '@/pages/Trips/Components/CustomerOptions';
import FadeLoader from 'vue-spinner/src/FadeLoader';

export default {
    name: 'EditOrderPage',
    components: {
        GoogleAutocomplete,
        TimePicker,
        LoadInputs,
        ChargingTypeOptions,
        TeamRegionOptions,
        CustomerOptions,
        FadeLoader
    },
    mixins: [GeneralMixin],
    computed: {
        ...mapGetters({
            user: 'user/user'
        }),
        itemDimensionFields() {
            if (!this.loadFieldList || this.loadFieldList.length === 0) {
                return [];
            }
            return this.loadFieldList.filter((loadDef) => loadDef.type !== 'quantity');
        }
    },
    data() {
        return {
            isLoading: true,
            isUpdating: false,
            hasPickupAddress: false,
            hasDeliveryAddress: false,
            hasAssignedDropStop: false,
            orderDetails: {
                orderNumber: null,
                sourceReference: null,
                purchaseOrderNumber: null,
                status: null,
                teamRegionId: null,
                customerId: null,
                brandId: null,
                rateGroupId: null,
                handlingMethod: null,
                orderDate: null,
                expectedDeliveryDate: null,
                readyPickupAvailableDate: null,
                readyDeliveryAvailableDate: null,
                timeWindowStart: null,
                timeWindowEnd: null,
                pickupTimeWindowStart: null,
                pickupTimeWindowEnd: null,
                contact: {
                    name: null,
                    phone: null,
                    email: null
                },
                pickupContact: {
                    name: null,
                    phone: null,
                    email: null
                },
                shippingAddress: {
                    name: null,
                    address: null,
                    location: {
                        latitude: null,
                        longitude: null
                    }
                },
                pickupAddress: {
                    name: null,
                    address: null,
                    location: {
                        latitude: null,
                        longitude: null
                    }
                },
                deliveryInstructions: null,
                notes: null,
                skills: [],
                items: []
            },
            errors: {},
            handlingMethodOptions: [],
            loadFieldList: [],
            loadValues: {},
            skillOptions: [],
            deliveryTimeWindowStart: 'None',
            deliveryTimeWindowEnd: 'None',
            pickupTimeWindowStart: 'None',
            pickupTimeWindowEnd: 'None'
        };
    },
    async mounted() {
        this.loadFieldList = this.user.vehicleCapacityUnitsConfiguration || [];
        if (this.user.skillOptions) {
            this.skillOptions = this.user.skillOptions.map((x) => x.name);
        }
        await Promise.all([
            this.fetchOrderDetails(),
            this.loadHandlingMethods()
        ]);
    },
    watch: {
        hasPickupAddress(newValue) {
            if (!newValue) {
                this.orderDetails.pickupAddress = {
                    name: null,
                    address: null,
                    location: { latitude: null, longitude: null }
                };
                this.orderDetails.pickupContact = {
                    name: null,
                    phone: null,
                    email: null
                };
                this.orderDetails.pickupTimeWindowStart = null;
                this.orderDetails.pickupTimeWindowEnd = null;
                this.pickupTimeWindowStart = 'None';
                this.pickupTimeWindowEnd = 'None';
            }
        },
        hasDeliveryAddress(newValue) {
            if (!newValue) {
                this.orderDetails.shippingAddress = {
                    name: null,
                    address: null,
                    location: { latitude: null, longitude: null }
                };
                this.orderDetails.contact = {
                    name: null,
                    phone: null,
                    email: null
                };
                this.orderDetails.timeWindowStart = null;
                this.orderDetails.timeWindowEnd = null;
                this.deliveryTimeWindowStart = 'None';
                this.deliveryTimeWindowEnd = 'None';
            }
        }
    },
    methods: {
        async fetchOrderDetails() {
            try {
                const { orderId } = this.$route.params;
                const api = `/api/orders/${orderId}`;
                const { data } = await handleRequests(api);

                this.orderDetails = {
                    orderNumber: data.orderNumber,
                    sourceReference: data.sourceReference,
                    purchaseOrderNumber: data.purchaseOrderNumber,
                    status: data.status,
                    teamRegionId: data.teamRegionId,
                    customerId: data.customerId,
                    brandId: data.brandId,
                    rateGroupId: data.rateGroupId,
                    handlingMethod: data.handlingMethod,
                    orderDate: data.orderDate ? new Date(data.orderDate) : null,
                    expectedDeliveryDate: data.expectedDeliveryDate ? new Date(data.expectedDeliveryDate) : null,
                    readyPickupAvailableDate: data.readyPickupAvailableDate ? new Date(data.readyPickupAvailableDate) : null,
                    readyDeliveryAvailableDate: data.readyDeliveryAvailableDate ? new Date(data.readyDeliveryAvailableDate) : null,
                    timeWindowStart: data.timeWindowStart,
                    timeWindowEnd: data.timeWindowEnd,
                    pickupTimeWindowStart: data.pickupTimeWindowStart,
                    pickupTimeWindowEnd: data.pickupTimeWindowEnd,
                    contact: {
                        name: data.contact?.name ?? null,
                        phone: data.contact?.phone ?? null,
                        email: data.contact?.email ?? null
                    },
                    pickupContact: {
                        name: data.pickupContact?.name ?? null,
                        phone: data.pickupContact?.phone ?? null,
                        email: data.pickupContact?.email ?? null
                    },
                    shippingAddress: data.shippingAddress || {
                        name: null,
                        address: null,
                        location: { latitude: null, longitude: null }
                    },
                    pickupAddress: data.pickupAddress || {
                        name: null,
                        address: null,
                        location: { latitude: null, longitude: null }
                    },
                    deliveryInstructions: data.deliveryInstructions,
                    notes: data.notes,
                    skills: data.skills || [],
                    items: []
                };

                // Populate items from the API response
                if (data.items && data.items.length > 0) {
                    this.orderDetails.items = data.items.map((item) => {
                        let loadObj = {};
                        if (item.load) {
                            try {
                                loadObj = typeof item.load === 'string' ? JSON.parse(item.load) : { ...item.load };
                            } catch (e) {
                                loadObj = {};
                            }
                        }
                        if (this.itemDimensionFields && this.itemDimensionFields.length > 0) {
                            this.itemDimensionFields.forEach((loadDef) => {
                                if (loadObj[loadDef.type] === undefined) {
                                    loadObj[loadDef.type] = null;
                                }
                            });
                        }
                        return {
                            itemId: item.itemId,
                            barcode: item.barcode,
                            sku: item.sku,
                            description: item.description,
                            status: item.status,
                            load: loadObj
                        };
                    });
                }

                // Set address toggles based on existing data
                this.hasPickupAddress = !!(data.pickupAddress && data.pickupAddress.address);
                this.hasDeliveryAddress = !!(data.shippingAddress && data.shippingAddress.address);

                // Pre-populate time window display values
                if (data.timeWindowStart) {
                    this.deliveryTimeWindowStart = this.extractTimeString(data.timeWindowStart);
                }
                if (data.timeWindowEnd) {
                    this.deliveryTimeWindowEnd = this.extractTimeString(data.timeWindowEnd);
                }
                if (data.pickupTimeWindowStart) {
                    this.pickupTimeWindowStart = this.extractTimeString(data.pickupTimeWindowStart);
                }
                if (data.pickupTimeWindowEnd) {
                    this.pickupTimeWindowEnd = this.extractTimeString(data.pickupTimeWindowEnd);
                }

                // Pre-populate load values from order data
                if (data.load) {
                    this.loadValues = typeof data.load === 'string' ? JSON.parse(data.load) : { ...data.load };
                }

                // Check if there are assigned drop stops
                await this.checkAssignedStops();

                this.isLoading = false;
            } catch (e) {
                const message = 'Could not retrieve order details. Please try again later.';
                showErrorMessage(this, message, e);
            }
        },
        async checkAssignedStops() {
            try {
                const { orderId } = this.$route.params;
                const api = `/api/orders/${orderId}/stops`;
                const response = await handleRequests(api);
                const stops = response.data?.responses || [];
                this.hasAssignedDropStop = stops.some(
                    (stop) => (stop.type === 'Drop' || stop.type == null) && stop.assignedTo?.publicUserId?.length
                );
            } catch {
                // Non-critical - just won't show the warning
            }
        },
        extractTimeString(dateTimeOffset) {
            if (!dateTimeOffset) return 'None';
            // Parse time directly from the ISO string to preserve the address timezone
            // e.g. "2026-03-26T14:00:00+11:00" -> "14:00"
            const match = dateTimeOffset.match(/T(\d{2}):(\d{2})/);
            if (match) {
                return `${match[1]}:${match[2]}`;
            }
            return 'None';
        },
        async loadHandlingMethods() {
            try {
                const endpoint = '/api/orders/handling-method';
                const response = await handleRequests(endpoint);
                if (response.data && response.data.length > 0) {
                    this.handlingMethodOptions = response.data;
                }
            } catch (error) {
                console.error('Error loading handling methods:', error);
            }
        },
        handleTeamRegionChanged(teamRegionId) {
            this.orderDetails.teamRegionId = teamRegionId || null;
        },
        handleCustomerChanged(customerId, customerModel) {
            this.orderDetails.customerId = customerId;
            if (customerModel?.defaultRateGroupId) {
                this.orderDetails.rateGroupId = customerModel.defaultRateGroupId;
            }
        },
        handleRateTypeChanged(rateRule) {
            this.orderDetails.rateGroupId = rateRule?.rateGroupId ?? null;
        },
        handleLoadChanged({ name, value }) {
            this.loadValues[name] = value;
        },
        handlePickupAddressChange(data) {
            if (data.addressComponents) {
                this.orderDetails.pickupAddress = {
                    ...this.orderDetails.pickupAddress,
                    address: data.address || '',
                    location: data.location,
                    ...data.addressComponents
                };
            } else {
                this.orderDetails.pickupAddress.address = data.address || '';
                this.orderDetails.pickupAddress.location = data.location;
            }
        },
        handleShippingAddressChange(data) {
            if (data.addressComponents) {
                this.orderDetails.shippingAddress = {
                    ...this.orderDetails.shippingAddress,
                    address: data.address || '',
                    location: data.location,
                    ...data.addressComponents
                };
            } else {
                this.orderDetails.shippingAddress.address = data.address || '';
                this.orderDetails.shippingAddress.location = data.location;
            }
        },
        getDeliveryTimeWindowStart(selectedTime) {
            if (selectedTime != null) {
                this.orderDetails.timeWindowStart = selectedTime.appointmentTime;
            } else {
                this.orderDetails.timeWindowStart = null;
            }
        },
        getDeliveryTimeWindowEnd(selectedTime) {
            if (selectedTime != null) {
                this.orderDetails.timeWindowEnd = selectedTime.appointmentTime;
            } else {
                this.orderDetails.timeWindowEnd = null;
            }
        },
        getPickupTimeWindowStart(selectedTime) {
            if (selectedTime != null) {
                this.orderDetails.pickupTimeWindowStart = selectedTime.appointmentTime;
            } else {
                this.orderDetails.pickupTimeWindowStart = null;
            }
        },
        getPickupTimeWindowEnd(selectedTime) {
            if (selectedTime != null) {
                this.orderDetails.pickupTimeWindowEnd = selectedTime.appointmentTime;
            } else {
                this.orderDetails.pickupTimeWindowEnd = null;
            }
        },
        addItem() {
            const load = {};
            if (this.itemDimensionFields && this.itemDimensionFields.length > 0) {
                this.itemDimensionFields.forEach((loadDef) => {
                    load[loadDef.type] = null;
                });
            }
            this.orderDetails.items.push({
                itemId: null,
                barcode: null,
                sku: null,
                description: null,
                status: null,
                load
            });
        },
        removeItem(index) {
            const item = this.orderDetails.items[index];
            if (item.itemId && item.status && item.status !== 'Created') {
                this.$notify({
                    message: `Cannot remove this item because it has status "${item.status}".`,
                    type: 'warning'
                });
                return;
            }
            this.orderDetails.items.splice(index, 1);
        },
        duplicateItem(index) {
            const originalItem = this.orderDetails.items[index];
            const clonedLoad = {};
            if (originalItem.load) {
                Object.keys(originalItem.load).forEach((key) => {
                    clonedLoad[key] = originalItem.load[key];
                });
            }
            let duplicatedBarcode = originalItem.barcode;
            if (duplicatedBarcode) {
                duplicatedBarcode = `${duplicatedBarcode}-1`;
            }
            const duplicatedItem = {
                itemId: null,
                barcode: duplicatedBarcode,
                sku: originalItem.sku,
                description: originalItem.description,
                status: null,
                load: clonedLoad
            };
            this.orderDetails.items.splice(index + 1, 0, duplicatedItem);
        },
        formatDisplayDate(date) {
            if (!date) return '';
            return moment(date).format(DATE_TYPES.standardDate);
        },
        formatDateForApi(date) {
            if (!date) return null;
            return moment(date).format(DATE_TYPES.internationalDate);
        },
        validateForm() {
            this.errors = {};
            let isValid = true;

            if (this.hasPickupAddress) {
                const pickupAddr = this.orderDetails.pickupAddress;
                if (!pickupAddr || !pickupAddr.address || pickupAddr.address.trim() === '') {
                    this.errors.pickupAddress = 'Pickup address is required when pickup address is enabled.';
                    isValid = false;
                }
            }

            if (this.hasDeliveryAddress) {
                const deliveryAddr = this.orderDetails.shippingAddress;
                if (!deliveryAddr || !deliveryAddr.address || deliveryAddr.address.trim() === '') {
                    this.errors.shippingAddress = 'Delivery address is required when delivery address is enabled.';
                    isValid = false;
                }
            }

            if (!isValid) {
                const errorMessages = Object.values(this.errors).join('\n');
                this.$notify({
                    message: errorMessages,
                    type: 'danger'
                });
            }

            return isValid;
        },
        cleanAddressForApi(address) {
            if (!address || !address.address) {
                return null;
            }

            const cleaned = {
                name: address.name || null,
                address: address.address
            };

            if (
                address.location &&
                typeof address.location.latitude === 'number' &&
                typeof address.location.longitude === 'number'
            ) {
                cleaned.location = {
                    latitude: address.location.latitude,
                    longitude: address.location.longitude
                };
            }

            if (address.line1) cleaned.line1 = address.line1;
            if (address.line2) cleaned.line2 = address.line2;
            if (address.city) cleaned.city = address.city;
            if (address.province) cleaned.province = address.province;
            if (address.provinceCode) cleaned.provinceCode = address.provinceCode;
            if (address.postcode) cleaned.postcode = address.postcode;
            if (address.country) cleaned.country = address.country;
            if (address.countryCode) cleaned.countryCode = address.countryCode;

            return cleaned;
        },
        cleanContactForApi(contact) {
            if (!contact) {
                return null;
            }

            const hasData = contact.name || contact.phone || contact.email;
            if (!hasData) {
                return null;
            }

            return {
                name: contact.name || null,
                phone: contact.phone || null,
                email: contact.email || null
            };
        },
        async updateOrder() {
            if (!this.validateForm()) {
                return;
            }

            this.isUpdating = true;
            this.$_handleLoaderState(true, 'Updating Order...');

            const shippingAddress = this.hasDeliveryAddress
                ? this.cleanAddressForApi(this.orderDetails.shippingAddress)
                : null;
            const pickupAddress = this.hasPickupAddress
                ? this.cleanAddressForApi(this.orderDetails.pickupAddress)
                : null;
            const contact = this.hasDeliveryAddress
                ? this.cleanContactForApi(this.orderDetails.contact)
                : null;
            const pickupContact = this.hasPickupAddress
                ? this.cleanContactForApi(this.orderDetails.pickupContact)
                : null;

            const data = {
                sourceReference: this.orderDetails.sourceReference || null,
                purchaseOrderNumber: this.orderDetails.purchaseOrderNumber || null,
                teamRegionId: this.orderDetails.teamRegionId,
                customerId: this.orderDetails.customerId,
                brandId: this.orderDetails.brandId,
                rateGroupId: this.orderDetails.rateGroupId,
                handlingMethod: this.orderDetails.handlingMethod,
                orderDate: this.orderDetails.orderDate
                    ? moment(this.orderDetails.orderDate).toISOString(true)
                    : null,
                expectedDeliveryDate: this.orderDetails.expectedDeliveryDate
                    ? moment(this.orderDetails.expectedDeliveryDate).toISOString(true)
                    : null,
                readyPickupAvailableDate: this.orderDetails.readyPickupAvailableDate
                    ? moment(this.orderDetails.readyPickupAvailableDate).toISOString(true)
                    : null,
                readyDeliveryAvailableDate: this.orderDetails.readyDeliveryAvailableDate
                    ? moment(this.orderDetails.readyDeliveryAvailableDate).toISOString(true)
                    : null,
                timeWindowStart: this.hasDeliveryAddress ? this.orderDetails.timeWindowStart : null,
                timeWindowEnd: this.hasDeliveryAddress ? this.orderDetails.timeWindowEnd : null,
                pickupTimeWindowStart: this.hasPickupAddress ? this.orderDetails.pickupTimeWindowStart : null,
                pickupTimeWindowEnd: this.hasPickupAddress ? this.orderDetails.pickupTimeWindowEnd : null,
                contact,
                pickupContact,
                shippingAddress,
                pickupAddress,
                deliveryInstructions: this.orderDetails.deliveryInstructions,
                notes: this.orderDetails.notes,
                skills: this.orderDetails.skills,
                load: Object.keys(this.loadValues).length > 0 ? this.loadValues : null,
                items: this.orderDetails.items.map((item) => {
                    const itemLoad = {};
                    if (item.load) {
                        Object.keys(item.load).forEach((key) => {
                            const value = parseFloat(item.load[key]);
                            if (!Number.isNaN(value) && value > 0) {
                                itemLoad[key] = value;
                                itemLoad.quantity = 1;
                            }
                        });
                    }
                    return {
                        itemId: item.itemId || null,
                        barcode: item.barcode,
                        sku: item.sku,
                        description: item.description,
                        load: Object.keys(itemLoad).length > 0 ? itemLoad : null
                    };
                })
            };

            const { orderId } = this.$route.params;
            const api = `/api/orders/${orderId}`;
            const payload = {
                method: 'put',
                data
            };

            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Order updated successfully.',
                    type: 'success'
                });
                this.$_handleLoaderState(false);
                this.$router.push({
                    name: 'Order Details',
                    params: { orderId }
                });
            } catch (e) {
                this.$_handleLoaderState(false);
                const message = 'Could not update order.';
                showErrorMessage(this, message, e);
            } finally {
                this.isUpdating = false;
            }
        }
    }
};
</script>

<style scoped lang="scss">
.loader-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;

    span {
        margin-top: 15px;
        color: #333;
    }
}

.back-link-container {
    margin-bottom: 10px;

    .back-link {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        color: #2196f3;
        text-decoration: none;
        font-size: 14px;
        font-weight: 500;

        &:hover {
            text-decoration: underline;
        }

        .md-icon {
            font-size: 18px !important;
            color: #2196f3;
        }
    }
}

.page-container {
    display: grid;
    grid-template-columns: 3fr 1fr 1fr;
    grid-gap: 20px;
}

@media (max-width: 1024px) {
    .page-container {
        grid-template-columns: 1fr 1fr;
        padding: 20px;
    }
}

@media (max-width: 768px) {
    .page-container {
        grid-template-columns: 1fr;
    }
}

.page-form-column {
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
}

.summary {
    position: sticky;
    top: 15px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-direction: column;
}

.summary-section {
    margin-bottom: 10px;
}

h5,
h6,
p {
    margin: 0;
}

h6 {
    margin-top: 5px;
}

p {
    margin-top: 2.5px;
}

::v-deep .md-card {
    z-index: initial !important;
    margin: 0;
}

::v-deep .md-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

::v-deep .md-card-actions {
    border: none;
}

.optional-label {
    font-size: 12px;
    color: #999;
    font-weight: normal;
}

.pickup-checkbox {
    margin: 0;
}

.load-inputs-row {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    margin-bottom: 15px;

    ::v-deep > * {
        flex: 1;
        min-width: 120px;
        max-width: 200px;
    }
}

.md-helper-text-warning {
    color: #ff5245;
    font-size: 12px;
    margin-top: 4px;
}

.no-items {
    text-align: center;
    padding: 30px;
    color: #999;
    background-color: #fafafa;
    border-radius: 4px;
}

.items-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.item-entry {
    padding: 15px;
    background-color: #fafafa;
    border-radius: 4px;
    border: 1px solid #eee;

    .item-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
        padding-bottom: 10px;
        border-bottom: 1px solid #eee;

        .item-number {
            font-size: 14px;
            font-weight: 600;
            color: #333;
        }

        .item-actions {
            display: flex;
            gap: 4px;
        }
    }
}

.item-status-badge {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 3px;
    background-color: #e3f2fd;
    color: #1976d2;
    margin-left: 8px;
    font-weight: 500;
}

.item-dimensions {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px dashed #ddd;

    .dimensions-header {
        margin-bottom: 10px;

        .dimensions-label {
            font-size: 12px;
            font-weight: 600;
            color: #666;
            text-transform: uppercase;
        }
    }

    .dimensions-inputs {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;

        .load-vals {
            flex: 1;
            min-width: 120px;
            max-width: 200px;

            span {
                font-size: smaller;
                margin-left: -12px;
            }
        }
    }
}

.timezone-note {
    display: block;
    font-size: 12px;
    color: #888;
    margin-top: 4px;
    font-style: italic;
}
</style>
